import { db } from '../db/index.js';
import {
  getLyricSheetsToPush,
  getLyricSheetTombstones,
  markLyricSheetPushed
} from '../db/operations.js';
import { apiClient, ApiError, ApiLyricSheet, LyricSheetUpsertBody, getLyricSheets, putLyricSheet } from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { isUuid, newId } from '../utils/id.js';
import type { LyricSheet } from '../types/index.js';
import { LIBRARY_CHANGED_EVENT } from './events.js';

/**
 * Lyric sheet sync (FRE-211), same shape as playlistSync: DELETE tombstones, PUT dirty sheets, then pull.
 * Latest write wins. Dirty rows wait for their owner to sign in; nothing is purged on logout.
 */

let inFlight: Promise<void> | null = null;
let rerunRequested = false;

const statusOf = (err: unknown): number | undefined => (err instanceof ApiError ? err.status : undefined);

/** Account guard: false when the signed-in account is no longer the one this pass started for. */
const sameAccount = (uid: string, token: string): boolean => {
  const auth = useAuthStore.getState();
  return auth.token === token && auth.user?.id === uid;
};

async function pushDeletes(uid: string, token: string): Promise<void> {
  for (const row of await getLyricSheetTombstones(uid)) {
    if (!sameAccount(uid, token)) return;
    if (!isUuid(row.id)) {
      await db.lyricSheets.delete(row.id); // never reachable by the server
      continue;
    }
    try {
      await apiClient.delete(`/api/lyrics/${row.id}`);
    } catch (err) {
      if (statusOf(err) !== 404) throw err; // 404 = already gone: counts as done
    }
    await db.lyricSheets.delete(row.id);
  }
}

async function rekeySheet(oldId: string): Promise<void> {
  await db.transaction('rw', db.lyricSheets, async () => {
    const cur = await db.lyricSheets.get(oldId);
    if (!cur) return;
    await db.lyricSheets.delete(oldId);
    const next: LyricSheet = { ...cur, id: newId(), dirty: 1, updatedAt: new Date().toISOString() };
    delete next.serverUpdatedAt;
    delete next.pushRejectedAt;
    await db.lyricSheets.put(next);
  });
}

/** `undefined` = skip this sheet for now (its tab is still uploading); otherwise the body to send. */
async function buildBody(row: LyricSheet, forceUnlink: boolean): Promise<LyricSheetUpsertBody | undefined> {
  let tabId: string | null = null;
  if (row.tabId && !forceUnlink) {
    const tab = await db.tabs.get(row.tabId);
    if (tab?.pendingUpload === 1) return undefined;
    if (isUuid(row.tabId)) tabId = row.tabId;
  }
  return { title: row.title, content: row.content, tab_id: tabId, created_at: row.createdAt };
}

async function pushUpserts(uid: string, token: string): Promise<void> {
  for (const row of await getLyricSheetsToPush(uid)) {
    if (!sameAccount(uid, token)) return;
    if (!isUuid(row.id)) {
      console.warn('lyricSync: skipping sheet with non-UUID id', row.id);
      continue;
    }
    if (row.pushRejectedAt && row.pushRejectedAt === row.updatedAt) continue;
    const snapshot = row.updatedAt;
    let unlink = false;
    for (;;) {
      const body = await buildBody(row, unlink);
      if (!body) break;
      try {
        const res = await putLyricSheet(row.id, body);
        if (!res || res.id !== row.id || typeof res.updatedAt !== 'string') {
          throw new Error('Unexpected lyric sheet response'); // transient: e.g. a catch-all mock
        }
        const outcome = await markLyricSheetPushed(row.id, snapshot, res.updatedAt, uid);
        if (outcome === 'gone') {
          try {
            await apiClient.delete(`/api/lyrics/${row.id}`);
          } catch (err) {
            if (statusOf(err) !== 404) throw err;
          }
        }
        break;
      } catch (err) {
        const status = statusOf(err);
        if (status === 404 && err instanceof ApiError && err.data.code === 'TAB_NOT_FOUND' && !unlink) {
          unlink = true; // tab never reaches the server: push once without the link, keep the local tabId
          continue;
        }
        if (status === 409) {
          await rekeySheet(row.id); // LYRIC_ID_TAKEN: retried under the new id on the next pass
          rerunRequested = true;
          break;
        }
        if (status === 400) {
          console.warn('lyricSync: server rejected lyric sheet', row.id);
          await db.lyricSheets.update(row.id, { pushRejectedAt: snapshot });
          break;
        }
        throw err;
      }
    }
  }
}

async function pullSheets(uid: string, token: string): Promise<void> {
  const before = await db.lyricSheets.toArray();
  const candidates = new Map<string, string>();
  for (const l of before) {
    if (l.ownerId === uid && l.dirty !== 1 && !l.deletedAt && l.serverUpdatedAt) {
      candidates.set(l.id, l.serverUpdatedAt);
    }
  }

  const res = await getLyricSheets();
  if (!res || !Array.isArray(res.data)) return;
  if (!sameAccount(uid, token)) return;
  const server: ApiLyricSheet[] = res.data;
  const serverIds = new Set(server.map((s) => s.id));

  let changed = false;
  await db.transaction('rw', db.lyricSheets, async () => {
    for (const s of server) {
      const fromServer: LyricSheet = {
        id: s.id,
        ownerId: uid,
        tabId: s.tabId,
        title: s.title,
        content: s.content,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        serverUpdatedAt: s.updatedAt,
        dirty: 0
      };
      const local = await db.lyricSheets.get(s.id);
      if (!local) {
        await db.lyricSheets.put(fromServer);
        changed = true;
        continue;
      }
      if (local.deletedAt || local.dirty === 1) continue;
      if (local.ownerId !== uid) continue;
      if (!local.serverUpdatedAt || s.updatedAt > local.serverUpdatedAt) {
        await db.lyricSheets.put(fromServer);
        changed = true;
      }
    }

    // "Absent from the list" means deleted elsewhere, but only when the list was not cut off at the cap.
    if (res.truncated !== false) return;
    for (const [id, snapshot] of candidates) {
      if (serverIds.has(id)) continue;
      const cur = await db.lyricSheets.get(id);
      if (cur && cur.dirty !== 1 && !cur.deletedAt && cur.ownerId === uid && cur.serverUpdatedAt === snapshot) {
        await db.lyricSheets.delete(id);
        changed = true;
      }
    }
  });

  if (changed) window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
}

async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  await pushDeletes(user.id, token);
  await pushUpserts(user.id, token);
  await pullSheets(user.id, token);
}

/** Push then pull. Single in-flight run; a call during a run schedules exactly one more. Guests: no-op. */
export function syncLyricSheets(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await runOnce();
      } while (rerunRequested);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Fire and forget; no-op offline; errors are swallowed. */
export function requestLyricSync(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  syncLyricSheets().catch((err) => {
    console.warn('lyricSync: background sync failed', err);
  });
}
